import { ProductItem } from '@/src/app/api/dialogApis/search.service';
import { cleanDescription, countries, getStoreDescription, safeProductId } from '@/src/lib/seo';
import { buildSectionImageUrl, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { generateSlug, slugify } from '@/src/lib/utils/slugify';
import { Routes } from '@/src/routes/routes';
import { BasicProductDataResponse } from '@/src/types/catalog';
import { IDigitalCard } from '@/src/types/digitalCards';
import { IBranch, IStore } from '@/src/types/stores';
import { Locale, useLocale, useTranslations } from 'next-intl';
import Script from 'next/script';
import { FC } from 'react';
import {
  FAQPage,
  Store,
  WithContext,
  Organization,
  WebSite,
  Graph,
  Product,
  ItemList,
  ListItem,
  Offer,
  BreadcrumbList,
  SearchResultsPage,
  CollectionPage,
} from 'schema-dts';

const MAX_LIST_ITEMS = 20;

interface LayoutStructuredDataProps {
  nonce?: string;
}

type ProductStrcutures = {
  name: string;
  description: string;
  image: string;
  url: string;
  sku: string;
  price: number;
  storeName: string;
  isSoldOut: boolean;
};

interface SearchStructuredDataProps {
  query: string;
  products: BasicProductDataResponse[];
  locale: string;
  relatedStores: IStore[];
}

export const company = {
  url: 'https://pik.sa',
  siteName: 'PIK',
  telephone: '8001241221',
  email: 'shops@jahez.net',
  logo: 'https://pik.sa/logo.svg',
  sameAs: [
    'https://www.instagram.com/pikappsa',
    'https://www.tiktok.com/@pikappsa',
    'https://x.com/pikappsa',
  ],
  address: {
    streetAddress: 'King Abdullah Road intersection with Abu Bakr Al Siddiq, Building Tharwa',
    addressLocality: 'Riyadh',
    addressRegion: 'Riyadh Province',
    postalCode: '11461',
    addressCountry: 'SA',
  },
};

// ------------------------------------------------------//
// ------------ Gigtal Card Structured Data ------------//
//------------------------------------------------------//

const DigitalCardStructuredData = ({
  locale,
  product,
}: {
  locale: string;
  product: IDigitalCard;
}) => {
  const isArabic = locale === 'ar';
  const url = `${company.url}/${locale}${Routes.digitalCardDetails(product.integrationId)}`;

  const productName = isArabic ? product.arabicName : product.englishName;
  const description = cleanDescription(
    isArabic ? product.arabicDescription : product.englishDescription,
  );

  const structuredData: Graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: productName,
        description: description,
        image: product.images.map((img) => img.url),
        brand: {
          '@type': 'Brand',
          name: productName,
        },
        offers: {
          '@type': 'AggregateOffer',
          url,
          priceCurrency: product.currencyCode || 'SAR',
          lowPrice: Math.min(...product.variants.map((v) => v.price)),
          highPrice: Math.max(...product.variants.map((v) => v.price)),
          offerCount: product.variants.length,
          availability: product.variants.some((v) => !v.isSoldOut)
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
          offers: product.variants.map((variant) => ({
            '@type': 'Offer',
            price: variant.price,
            priceCurrency: product.currencyCode || 'SAR',
            availability: variant.isSoldOut
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            url,
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: variant.price,
              priceCurrency: product.currencyCode || 'SAR',
            },
          })),
        },
        category: product.categories
          .map((cat) => (isArabic ? cat.arabicName : cat.englishName))
          .join(', '),
        productID: product.integrationId,
        sku: product.integrationId,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: isArabic ? 'الرئيسية' : 'Home',
            item: company.url,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: isArabic ? 'المتجر الرقمي' : 'Digital Store',
            item: `${company.url}/store`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: productName,
            item: url,
          },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
      }}
    />
  );
};

// ------------------------------------------------------//
// ------------ Gigtal Cards Structured Data ------------//
//------------------------------------------------------//

const DigitalCardsStructuredData = ({
  cards,
  locale,
  query,
  categoryName,
  categoryId,
}: {
  locale: string;
  query: string;
  cards: IDigitalCard[];
  categoryName: string;
  categoryId: string;
}) => {
  const isArabic = locale === 'ar';
  const url = `${company.url}/${locale}${Routes.digitalCards}`;

  const faqs = isArabic
    ? [
        {
          question: 'كيف أستلم البطاقة الرقمية؟',
          answer:
            'بعد إتمام عملية الشراء، ستصلك البطاقة الرقمية فوراً عبر البريد الإلكتروني خلال دقائق معدودة.',
        },
        {
          question: 'هل يمكنني استرجاع البطاقة؟',
          answer:
            'البطاقات الرقمية غير قابلة للاسترجاع بعد الشراء، يرجى التأكد من اختيارك قبل إتمام الطلب.',
        },
        {
          question: 'ما هي وسائل الدفع المتاحة؟',
          answer: 'نوفر عدة وسائل دفع آمنة تشمل: بطاقات الائتمان، مدى، آبل باي، STC Pay وغيرها.',
        },
        {
          question: 'هل البطاقات لها تاريخ انتهاء صلاحية؟',
          answer: 'نعم، كل بطاقة لها تاريخ صلاحية محدد، يمكنك الاطلاع عليه في تفاصيل البطاقة.',
        },
      ]
    : [
        {
          question: 'How do I receive the digital card?',
          answer:
            'After completing your purchase, you will receive the digital card instantly via email within minutes.',
        },
        {
          question: 'Can I return a digital card?',
          answer:
            'Digital cards are non-refundable after purchase. Please ensure your selection before completing the order.',
        },
        {
          question: 'What payment methods are available?',
          answer:
            'We offer several secure payment methods including: Credit cards, Mada, Apple Pay, STC Pay and more.',
        },
        {
          question: 'Do cards have an expiration date?',
          answer:
            'Yes, each card has a specific expiration date that you can view in the card details.',
        },
      ];

  const graphNodes: any[] = [];

  graphNodes.push({
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  });

  // If there's a query
  if (query) {
    if (cards && cards.length > 0) {
      graphNodes.push({
        '@type': 'SearchResultsPage',
        name: isArabic ? `نتائج البحث عن: ${query}` : `Search results for: ${query}`,
        description: isArabic
          ? `تم العثور على ${cards.length} نتيجة`
          : `Found ${cards.length} results`,
        url: `${url}?query=${encodeURIComponent(query)}`,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: cards.length,
          itemListElement: cards.slice(0, 10).map((card, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Product',
              '@id': `${url}${Routes.digitalCardDetails(card.integrationId)}`,
              name: isArabic ? card.arabicName : card.englishName,
              image: card.defaultImage,
              url: `${url}${Routes.digitalCardDetails(card.integrationId)}`,
              offers: {
                '@type': 'Offer',
                price: card.price,
                priceCurrency: card.currencyCode,
                availability: card.isSoldOut
                  ? 'https://schema.org/OutOfStock'
                  : 'https://schema.org/InStock',
              },
            },
          })),
        },
      });
    } else {
      // query exists but no results -> add only no-results node (no duplicate search node)
      graphNodes.push({
        '@type': 'SearchResultsPage',
        name: isArabic ? `بحث: ${query}` : `Search: ${query}`,
        description: isArabic ? 'لم يتم العثور على نتائج' : 'No results found',
        url: `${url}?query=${encodeURIComponent(query)}`,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: 0,
        },
      });
    }
  } else if (categoryId && cards && cards.length > 0) {
    // category page (only when no query and category present)
    graphNodes.push({
      '@type': 'CollectionPage',
      name: categoryName,
      description: isArabic
        ? `${cards.length} بطاقة في هذه الفئة`
        : `${cards.length} cards in this category`,
      url: `${url}?category=${categoryId}`,
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: cards.length,
        itemListElement: cards.map((card, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: isArabic ? card.arabicName : card.englishName,
            image: card.defaultImage,
            url: `${url}${Routes.digitalCardDetails(card.integrationId)}`,
          },
        })),
      },
    });
  }

  const structuredData: Graph = {
    '@context': 'https://schema.org',
    '@graph': graphNodes,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
      }}
    />
  );
};

// ------------------------------------------------------//
// ---------------- Store Structured Data --------------//
//------------------------------------------------------//

const StoreStructuredData = ({ locale, data }: { locale: Locale; data: any }) => {
  if (!data?.BranchDetails?.store) return null;

  const {
    store,
    countryId,
    schedules,
    orderTypes,
    currencyCode,
    latitude,
    longitude,
    rating,
    reviewCount,
  } = data.BranchDetails;
  const storeName = locale === 'ar' ? store.arabicName : store.englishName;
  const slug = slugify(store.id, store.englishName);
  const url = `${company.url}/${locale}/stores/${slug}`;
  const imageUrl = buildSectionImageUrl(store.logoUrl ?? '', PRODUCT_IMAGE_SIZE.MEDIUM);

  const openingHours = schedules
    ?.flatMap((s: any) =>
      s.branchScheduleDays
        ?.filter((d: any) => !d.isClosed)
        .flatMap((d: any) =>
          d.branchScheduleOpeningHours?.map((h: any) => ({
            '@type': 'OpeningHoursSpecification' as const,
            dayOfWeek: d.englishDayString,
            opens: h.startHour,
            closes: h.endHour,
          })),
        ),
    )
    .filter(Boolean);

  const makesOffer: Offer[] | undefined = orderTypes?.length
    ? orderTypes.map((o: any) => ({
        '@type': 'Offer' as const,
        availability: 'https://schema.org/InStock',
        itemOffered: {
          '@type': 'Service' as const,
          name: locale === 'ar' ? o.arabicTypeString : o.englishTypeString,
        },
      }))
    : undefined;

  const structuredData: WithContext<Store> = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': url,
    name: storeName,
    telephone: company.telephone,
    priceRange: 'SAR',
    description: getStoreDescription({
      description: locale === 'ar' ? store.arabicDescription : store.englishDescription,
      storeName,
      countryId,
      locale,
    }),
    url,
    image: imageUrl
      ? [{ '@type': 'ImageObject', url: imageUrl, width: 600, height: 600 }]
      : undefined,
    address: {
      '@type': 'PostalAddress',

      addressCountry: {
        '@type': 'Country',
        name: countryId === 193 ? 'SA' : countryId === 17 ? 'BH' : 'KW',
      },
    },
    legalName: storeName,
    currenciesAccepted: currencyCode || 'SAR',
    ...(latitude &&
      longitude && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude,
          longitude,
        },
      }),
    ...(rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        reviewCount: reviewCount || 1,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    ...(openingHours?.length ? { openingHoursSpecification: openingHours } : {}),
    ...(makesOffer ? { makesOffer } : {}),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData).replace(/</g, '\\u003c') }}
    />
  );
};

//----------------------------------------------------------------//
//---------------Breadcrumb Store Structured Data ----------------//
//----------------------------------------------------------------//

const BreadcrumbStoreStructuredData = ({
  locale,
  storeName,
  slug,
}: {
  locale: string;
  storeName: string;
  slug: string;
}) => {
  const t = {
    home: locale === 'ar' ? 'الرئيسية' : 'Home',
    stores: locale === 'ar' ? 'المتاجر' : 'Stores',
  };
  const base = `${company.url}/${locale}`;

  const breadcrumb: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t.home, item: `${base}/` },
      { '@type': 'ListItem', position: 2, name: t.stores, item: `${base}/stores` },
      { '@type': 'ListItem', position: 3, name: storeName, item: `${base}/stores/${slug}` },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb).replace(/</g, '\\u003c') }}
    />
  );
};
//----------------------------------------------------------------//
// ------------------------- Store FAQ --------------------------//
//----------------------------------------------------------------//

const StoreFAQ = ({ locale, storeName }: { locale: 'en' | 'ar'; storeName: string }) => {
  const storefaqData: WithContext<FAQPage> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity:
      locale === 'en'
        ? [
            {
              '@type': 'Question',
              name: `What services does ${storeName} offer?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `${storeName} offers Instant, Scheduled, and Global Delivery options, depending on your location. You can also send your order as a gift`,
              },
            },
            {
              '@type': 'Question',
              name: `What are the opening hours of ${storeName}?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Opening hours vary by day. Please check the schedule on the store page for detailed timings.',
              },
            },
            {
              '@type': 'Question',
              name: `Does ${storeName} accept returns?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, return policies may apply. Please review the return terms on the store page.',
              },
            },
            {
              '@type': 'Question',
              name: `Where is ${storeName} located?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `The store is located in your country. Please review the branch locations on the store page.`,
              },
            },
            {
              '@type': 'Question',
              name: `Does ${storeName}  support Buy Now, Pay Later?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `Yes, ${storeName} offers Buy Now, Pay Later options.`,
              },
            },
          ]
        : [
            {
              '@type': 'Question',
              name: `ما هي الخدمات التي يقدمها ${storeName} `,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `يقدم ${storeName} خيارات التوصيل الفوري، المجدول، والتوصيل العالمي حسب موقعك. يمكنك أيضًا إرسال طلبك كهدية.`,
              },
            },
            {
              '@type': 'Question',
              name: `ما هي أوقات عمل ${storeName}`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `تختلف أوقات العمل حسب اليوم. يُرجى مراجعة الجدول في صفحة المتجر لمعرفة المواعيد بالتفصيل.`,
              },
            },
            {
              '@type': 'Question',
              name: `هل يقبل ${storeName}  الاسترجاع؟`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'نعم، قد تنطبق سياسات الإرجاع. يُرجى مراجعة شروط الإرجاع في صفحة المتجر.',
              },
            },
            {
              '@type': 'Question',
              name: `أين يقع ${storeName}`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `يقع المتجر داخل بلدك. يُرجى مراجعة مواقع الفروع في صفحة المتجر.`,
              },
            },
            {
              '@type': 'Question',
              name: `هل يدعم ${storeName}  خدمة اشتر الآن وادفع لاحقاً؟`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `نعم، يقدم ${storeName} خيارات "اشترِ الآن وادفع لاحقًا".`,
              },
            },
          ],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(storefaqData).replace(/</g, '\\u003c') }}
    />
  );
};
//----------------------------------------------------------------//
/*------------------ start layout structure data ----------------- */
//----------------------------------------------------------------//

const LayoutStructuredData = ({ nonce }: LayoutStructuredDataProps) => {
  const t = useTranslations('SEO.company');

  const jsonLd: Graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        image: `${company.url}/pik-logo.svg`,
        name: 'PIK',
        alternateName: 'بِك',
        description: t('description'),
        url: company.url,
        sameAs: company.sameAs,
        logo: company.logo,
        telephone: company.telephone,
        email: company.email,
        address: {
          '@type': 'PostalAddress',
          ...company.address,
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: company.telephone,
            contactType: 'customer service',
            email: company.email,
            areaServed: 'SA',
            availableLanguage: ['English', 'Arabic'],
          },
        ],
        areaServed: {
          '@type': 'Country',
          name: 'Saudi Arabia',
        },
      },
    ],
  };

  return (
    <Script
      type="application/ld+json"
      id="layout-structured-data"
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c'),
      }}
    />
  );
};

const HomeStructuredData = () => {
  const t = useTranslations('SEO.home');
  const locale = useLocale();

  const jsonLd: Graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: t('name'),
        description: t('description'),
        inLanguage: ['ar', 'en'],
        url: company.url,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${company.url}/${locale}/search?query={search_term_string}`,
          query: 'required name=search_term_string',
        },
      },
      {
        '@type': 'MobileApplication',
        name: 'PIK',
        description: t('description'),
        applicationCategory: 'LifestyleApplication',
        operatingSystem: ['iOS', 'Android'],
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'SAR',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '509',
        },
        downloadUrl: [
          'https://apps.apple.com/us/app/pik-%D8%A8%D9%83/id1551136726',
          'https://play.google.com/store/apps/details?id=com.pik.pikapp&hl=ar',
        ],
        publisher: {
          '@type': 'Organization',
          name: 'PIK',
          url: 'https://pik.sa',
        },
      },
      {
        '@type': 'Service',
        name: t('service'),
        description: t('description'),
        serviceType: 'Delivery Service',
        areaServed: {
          '@type': 'Country',
          name: 'Saudi Arabia',
        },
      },
    ],
  };

  return (
    <Script
      type="application/ld+json"
      id="home-schema"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c'),
      }}
    />
  );
};

//----------------------------------------------------------------//
// -------------------- Product Structured Data ------------------//
//----------------------------------------------------------------//

const ProductStructuredData = ({
  description,
  image,
  name,
  price,
  sku,
  storeName,
  url,
  isSoldOut,
}: ProductStrcutures) => {
  const availability = isSoldOut ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';

  const structuredData: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: [buildSectionImageUrl(image ?? '', PRODUCT_IMAGE_SIZE.MEDIUM)],
    url: url,
    sku,
    mpn: sku,
    brand: {
      '@type': 'Brand',
      name: storeName,
    },
    offers: {
      '@type': 'Offer',
      '@id': `${url}#offer`,
      url,
      priceCurrency: 'SAR',
      price: price.toFixed(2),
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: storeName,
      },
      // availability: 'InStock',
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData).replace(/</g, '\\u003c') }}
    />
  );
};

//----------------------------------------------------------------//
// -------------------- Search Structured Data ------------------
//----------------------------------------------------------------//

export default function SearchStructuredData({
  query,
  products,
  relatedStores,
  locale,
}: SearchStructuredDataProps) {
  const isArabic = locale === 'ar';

  const allItems: ListItem[] = [
    ...products.slice(0, MAX_LIST_ITEMS).map(
      (product, index): ListItem => ({
        position: index + 1,
        '@type': 'ListItem',
        item: {
          '@type': 'Product',
          name: isArabic ? (product.arabicName ?? '') : (product.englishName ?? ''),
          description: isArabic
            ? (product.arabicDescription ?? '')
            : (product.englishDescription ?? ''),
          image: buildSectionImageUrl(product.defaultImage ?? '', PRODUCT_IMAGE_SIZE.MEDIUM),
          sku: product.integrationId,
          mpn: product.integrationId,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'SAR',
            price: product.price?.toFixed(2) ?? '0',
            availability: product.isSoldOut
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: product.storeName ?? '',
            },
          },
          url: `${company.url}/${locale}/stores/${product.storeId}/product/${generateSlug(
            product.integrationId,
            product.englishName ?? '',
          )}`,
        } as Product,
      }),
    ),
    ...relatedStores.slice(0, MAX_LIST_ITEMS).map(
      (store, index): ListItem => ({
        '@type': 'ListItem',
        position: products.length + index + 1,
        item: {
          '@type': 'Store',
          name: isArabic ? (store.arabicName ?? '') : (store.englishName ?? ''),
          image: buildSectionImageUrl(store.store.logoUrl ?? '', PRODUCT_IMAGE_SIZE.MEDIUM),
          url: `${company.url}/${locale}/${slugify(store.store.id, store.store.englishName)}`,
        } as Store,
      }),
    ),
  ];

  if (allItems.length === 0) {
    const emptyStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: isArabic ? `نتائج البحث عن "${query}"` : `Search results for "${query}"`,
      description: isArabic
        ? `لم يتم العثور على نتائج لـ "${query}" على Pik.`
        : `No results found for "${query}" on Pik.`,
      url: `${company.url}/${locale}/search?query=${encodeURIComponent(query)}`,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${company.url}/${locale}/search?query={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(emptyStructuredData).replace(/</g, '\\u003c'),
        }}
      />
    );
  }

  // ✅ في حالة وجود نتائج (Products أو Stores)
  const structuredData: WithContext<ItemList> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: isArabic ? `نتائج البحث عن "${query}"` : `Search results for "${query}"`,
    description: isArabic
      ? `استكشف أفضل النتائج عن "${query}" من المنتجات والمتاجر على Pik.`
      : `Explore top products and stores related to "${query}" on Pik.`,
    numberOfItems: allItems.length,
    itemListElement: allItems,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
      }}
    />
  );
}

// ------------------------------------------------------//
// -------------- Stores Index Structured Data ----------//
// ------------------------------------------------------//

const StoresIndexStructuredData = ({ locale }: { locale: string }) => {
  const isArabic = locale === 'ar';
  const url = `${company.url}/${locale}/stores`;

  const structuredData: Graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: isArabic ? 'دليل المتاجر - بِك' : 'Stores Directory - Pik',
        description: isArabic
          ? 'تصفح جميع المتاجر والماركات المسجلة في تطبيق وموقع بِك للتوصيل السريع.'
          : 'Browse all registered stores and top brands delivering via Pik.',
        url,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: isArabic ? 'الرئيسية' : 'Home',
            item: `${company.url}/${locale}`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: isArabic ? 'المتاجر' : 'Stores',
            item: url,
          },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
      }}
    />
  );
};

export {
  StoreStructuredData,
  BreadcrumbStoreStructuredData,
  StoreFAQ,
  LayoutStructuredData,
  HomeStructuredData,
  ProductStructuredData,
  SearchStructuredData,
  DigitalCardStructuredData,
  DigitalCardsStructuredData,
  StoresIndexStructuredData,
};
